/* eslint-disable @typescript-eslint/no-dynamic-delete */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { remoteSchemeCommonToBanner } from 'utils/adapters'
import { JSONParser } from '@streamparser/json-whatwg'
import { BLACKLISTED_KEYS } from 'utils/storage'
import { unescapeVariantHTML } from 'utils/mapPublicBanners'
import { type BannerTypeRender } from 'utils/types'

const BLACKLISTED_VARIANT_KEYS = ['codePreview', 'dynamicYieldVariationId', 'metadata', 'responsiveImage', 'templateName']
const BLACKLISTED_CODE_KEYS = ['componentName', 'props', 'raw', 'transpiledCode']

const BUFFER_SIZE = 25
const BUFFER_TIMEOUT = 100 // ms

const processVariant = (v: any) => {
  delete v?.preview?.uuid
  if (typeof v.code === 'object') {
    for (const key of BLACKLISTED_CODE_KEYS) delete v.code[key]
  }
  for (const key of BLACKLISTED_VARIANT_KEYS) delete v[key]

  const variant = { ...v }
  if (v.code?.compiled ?? v.code?.html) {
    variant.code = {
      compiled: v.code?.compiled,
      html: unescapeVariantHTML(v.code?.html)
    }
  }

  return variant
}

const useStreamResponse = ({
  url,
  streamCallback
}: {
  url: string
  streamCallback: React.Dispatch<BannerTypeRender[]>
}) => {
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const cachedData = queryClient.getQueryData<BannerTypeRender[]>(['publicBanners']) ?? []
  const streamData = useRef<BannerTypeRender[]>(cachedData)
  const [finalData, setFinalData] = useState<BannerTypeRender[]>(cachedData)

  const bufferTimeout = useRef<NodeJS.Timeout>()
  const updateBuffer = useRef<BannerTypeRender[]>([])
  const flushBuffer = useCallback(() => {
    if (updateBuffer.current.length > 0) {
      streamData.current = [...streamData.current, ...updateBuffer.current]
      streamCallback(streamData.current)
      updateBuffer.current = []
    }
  }, [streamCallback])

  const scheduleFlush = useCallback(() => {
    if (bufferTimeout.current) {
      clearTimeout(bufferTimeout.current)
    }
    bufferTimeout.current = setTimeout(flushBuffer, BUFFER_TIMEOUT)
  }, [flushBuffer])

  const parser = useMemo(() => new JSONParser({
    stringBufferSize: undefined,
    paths: ['$.*'],
    keepStack: false
  }), [])

  const processBannerValue = useCallback((value: any) => {
    if (!value || streamData.current.some((b) => b.id === value.id)) {
      return null
    }

    // value.location is legacy and copied into locations above
    // delete from stored data to let us load quicker + skip location check in bannersForLocation
    BLACKLISTED_KEYS.concat('location').forEach((key: string) => delete value[key])
    const processedValue = {
      ...value,
      locations: Array.isArray(value.locations) ? value.locations : (value.location ? [value.location] : []),
      screenTypes: remoteSchemeCommonToBanner(value)?.screenTypes,
      webSlots: value.webSlots ?? [],
      variants: value?.variants.map(processVariant)
    }

    updateBuffer.current.push(processedValue)

    if (updateBuffer.current.length >= BUFFER_SIZE) {
      flushBuffer()
    } else {
      scheduleFlush()
    }

    return processedValue
  }, [flushBuffer, scheduleFlush])

  const { mutate: startStream } = useMutation({
    mutationFn: async () => {
      const res = await fetch(url)
      if (!res.body) {
        throw new Error('ReadableStream not supported in this browser.')
      }
      return res.body.pipeThrough(parser).getReader()
    },
    onSuccess: async (reader) => {
      setIsLoading(true)
      await readStream(reader)
      queryClient.setQueryData(['publicBanners'], streamData.current)
      setFinalData(streamData.current)
    }
  })

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (bufferTimeout.current) {
        clearTimeout(bufferTimeout.current)
      }
    }
  }, [])

  const readStream = useCallback(async (reader: ReadableStreamDefaultReader) => {
    try {
      while (true) {
        const { done, value: parsedElementInfo } = await reader.read()
        if (done) {
          flushBuffer()
          break
        }
        processBannerValue(parsedElementInfo.value)
      }
    } finally {
      setIsLoading(false)
      reader.releaseLock()
    }
  }, [flushBuffer, processBannerValue])

  return { data: finalData, startStream, isLoading }
}

export default useStreamResponse
